/**
 * Hand-written client for the Ringil Chat Assistant service (PoC).
 *
 * The generated client (`ringil-api.v2.tsx`) is bound to `/services/frontend-service` and speaks a
 * response envelope (`{ payload, errorType }`) plus `runAs`/`trcUUID` query params. The assistant is a
 * different service at a different prefix with a plain FastAPI contract, so it gets its own small
 * fetch-based client instead of being squeezed into `apiRuntime`.
 *
 * Contract: tmp/assistant/assistant-swagger.json (fetched from alpha's /services/assistant/openapi.json).
 * Answers are followed over `/chat/requests/{id}/stream` — Server-Sent Events, with polling
 * `getRequestStatus` as the fallback; see useAssistantChat. The spec publishes the route without a
 * schema; observed on alpha (2026-09-18):
 *   event: status  data: {"step": "retrieve_evidence"}      pipeline progress, one per stage
 *   event: token   data: {"text": "Přepravu si můžete…"}    the answer's markdown, in pieces, in order
 *   event: done    data: <ChatRequestStatus>                 the final status, as getRequestStatus returns it
 */
import { createLogger } from '@utils/logger';
import { SseEvent, readSseStream } from './sse';

const log = createLogger('assistantApi');

/**
 * Same-origin, exactly like the frontend-service client. The assistant sits behind the same gateway as
 * the rest of the backend, so the existing `/services` → BACKEND_URL dev proxy already covers it.
 */
export const ASSISTANT_BASE_URL = '/services/assistant';

export type ChatRole = 'USER' | 'ASSISTANT' | 'SYSTEM_SUMMARY';
export type ChatRequestState = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
/** CLARIFICATION: the question was ambiguous — the answer asks back and offers `suggestions` to pick from. */
export type AssistantOutcome = 'ANSWERED' | 'INSUFFICIENT_EVIDENCE' | 'GUARDED' | 'CLARIFICATION';
export type RouteDisposition = 'KNOWLEDGE_QUESTION' | 'DATA_QUESTION' | 'ACTION_REQUEST' | 'OUT_OF_DOMAIN';

export interface EvidenceCitation {
  /** Stable id of the source document — the only identifier every citation has. */
  doc_id: string;
  /** Where the source can be opened. Not every source has one, so a citation is not always a link. */
  source_uri?: string | null;
  title?: string | null;
  snippet: string;
  rank: number;
}

/** One of the answers offered with a CLARIFICATION; picking it sends `text` as the next question. */
export interface ClarificationSuggestion {
  text: string;
  description?: string | null;
  rank: number;
}

export interface AssistantAnswer {
  outcome: AssistantOutcome;
  content_markdown: string;
  citations?: EvidenceCitation[];
  suggestions?: ClarificationSuggestion[];
}

export interface ChatMessageView {
  role: ChatRole;
  content: string;
  created_at: string;
  citations?: EvidenceCitation[];
  suggestions?: ClarificationSuggestion[];
}

export interface PageResponseChatMessageView {
  data: ChatMessageView[];
  total: number;
  page: number;
  size: number;
}

export interface ChatSessionView {
  session_id: string;
  status: 'ACTIVE' | 'ARCHIVED';
  active_goal?: string;
  latest_summary?: string;
  recent_messages: PageResponseChatMessageView;
  last_activity_at: string;
}

export interface ChatRequestSubmission {
  session_id?: string;
  message: string;
  /** What was on screen when the question was sent; omitted when the snapshot could not be built. */
  page_context?: PageContext;
}

/**
 * A bounded description of the user's screen, so the assistant can explain a message, a failed call or
 * a read-only area without a screenshot. Built in pageContext/snapshot.ts. Never carries tokens, URLs,
 * query strings, request bodies or anything the user typed into a form.
 */
export interface PageContext {
  version: 1;
  /** ISO 8601 with offset, when the snapshot was taken. */
  captured_at: string;
  app_release: string | null;
  impersonating: boolean;
  /** The route pattern (`/reservations/:id`), never the actual path. */
  route: { pattern: string; params: Record<string, string> } | null;
  /** At most 10: on screen first, then most recently gone. */
  messages: ScreenMessage[];
  /** At most 20, page content only. */
  blocked: BlockedArea[];
  /** At most 15: hidden or read-only menu entries (ids starting with `menu-`). */
  blocked_navigation: BlockedArea[];
  /** At most 20, newest `last_at` first. */
  unresolved_errors: UnresolvedError[];
}

export type ScreenMessageVariant = 'info' | 'warning' | 'error' | 'success' | 'alert';

export interface ScreenMessage {
  /** The translation key passed to tr(). */
  key: string;
  params: string[];
  /** Optional reason enum, e.g. 'SLOT_OCCUPIED'. */
  code: string | null;
  variant: ScreenMessageVariant;
  on_screen: boolean;
  /** When it disappeared; null while on screen. */
  last_seen_at: string | null;
}

export type UnmetClauseType = 'permission' | 'feature' | 'module' | 'state' | 'ownership' | 'role' | 'data' | 'custom';

export interface UnmetClause {
  type: UnmetClauseType;
  /** What is not true, e.g. 'logged company owns the notification'; null when the clause has no label. */
  label: string | null;
  /** Permission / feature / module names when applicable. */
  any_of: string[] | null;
  /** The id of the flag this clause belongs to; null for a clause declared inline on the area. */
  rule: string | null;
  /** What the rule is for — the clause's own description, else its flag's. */
  description: string | null;
  /** true: blocked on purpose in this situation; false: something can change it; null: not written. */
  by_design: boolean | null;
}

export interface BlockedArea {
  /** SecuredArea id without the instance part, e.g. 'row-waiting-payment'. */
  id: string;
  /** The instances blocked for this reason (row id, field key…), at most 10; null when the area is not repeated. */
  instances: string[] | null;
  label: string | null;
  /** What the section is and whose it is; null when not written. */
  description: string | null;
  /** The area's non-security context note (e.g. why a mass action is greyed out); null when none. */
  note: string | null;
  /** Hidden, or shown but read-only. */
  blocked: 'visible' | 'editable';
  /** All blocked registrations in this entry, including instances beyond the 10 listed. */
  count: number;
  /** Only the clauses that are not satisfied. */
  unmet: UnmetClause[];
}

export interface ErrorMessage {
  /** The message when the backend marked it `locized`. */
  key: string | null;
  /** The message when it is not a translation key. */
  text: string | null;
  params: string[];
  scope: string;
  path: string;
}

export interface UnresolvedError {
  /** Generated client method name, e.g. 'updateReservationTimeslot'. */
  method: string;
  http_method: string;
  /** null for network errors. */
  status: number | null;
  /** Envelope errorType: 'VALIDATION', 'CONFLICT', 'ERROR', ... */
  error_type: string | null;
  /** Axios code for network errors, e.g. 'ERR_NETWORK'. */
  error_code: string | null;
  /** First failure of this call since it last succeeded. */
  first_at: string;
  last_at: string;
  /** Failures of this call since it last succeeded. */
  count: number;
  trc_uuid: string | null;
  /** Of the latest failure, at most 5. */
  messages: ErrorMessage[];
}

export interface ChatRequestAccepted {
  request_id: string;
  session_id: string;
  status: 'PENDING';
  status_url: string;
}

export interface ChatRequestStatus {
  request_id: string;
  session_id: string;
  status: ChatRequestState;
  user_message: string;
  entities?: string[];
  language_hint?: string;
  route_disposition?: RouteDisposition;
  trace_id?: string;
  answer?: AssistantAnswer;
  error_message?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

/**
 * The stream endpoint answered, but not with an event stream: an error status, or a plain response
 * (a proxy that buffers, a backend without streaming). The caller should fall back to polling.
 */
export class AssistantStreamUnavailableError extends Error {
  constructor(readonly status: number, readonly contentType: string | null) {
    super(`Assistant stream unavailable (${status}, ${contentType ?? 'no content type'})`);
    this.name = 'AssistantStreamUnavailableError';
  }
}

/** The server sent an `error` event without a final status — the request's outcome is left to polling. */
export class AssistantStreamFailedError extends Error {
  constructor(readonly data: string) {
    super(`Assistant stream reported an error: ${data}`);
    this.name = 'AssistantStreamFailedError';
  }
}

/** What a session stream reports on top of a request stream's events. */
export interface AssistantSessionStreamHandlers extends AssistantStreamHandlers {
  /**
   * The `snapshot` event sent on attach — names the request in flight, if any. Extra information, not
   * the sign that the stream is up: that is `onOpen`, which does not depend on the event's name.
   */
  onAttached: (snapshot: Record<string, unknown>) => void;
  /** An `error` event with no status. On a session stream one request's error must not end the stream. */
  onRequestError: (requestId: string | undefined, data: string) => void;
}

export interface AssistantStreamHandlers {
  /** The response is a live event stream — called before any event. */
  onOpen?: () => void;
  /** A pipeline stage started (`status` event), e.g. `retrieve_evidence`. `requestId` only on a session stream, and only when the server names it. */
  onStep: (step: string, requestId?: string) => void;
  /** The next piece of the answer's markdown (`token` event). */
  onToken: (text: string, requestId?: string) => void;
  /** A full request status — the `done` event, or any event that carries one. */
  onStatus: (status: ChatRequestStatus) => void;
  /** Every event or keep-alive comment, for idle detection. */
  onAlive: () => void;
}

/** Thrown for any non-2xx response so callers can distinguish HTTP failures from network/abort errors. */
export class AssistantApiError extends Error {
  constructor(readonly status: number, readonly url: string, readonly body?: string) {
    super(`Assistant API ${status} for ${url}`);
    this.name = 'AssistantApiError';
  }
}

export interface AssistantApiOptions {
  /** Current raw Auth0 ID token. */
  getToken: () => Promise<string | undefined>;
  /** Forces a fresh ID token; used once on 401, mirroring the frontend-service client. */
  refreshToken: () => Promise<string | undefined>;
}

export interface AssistantApi {
  submitRequest: (body: ChatRequestSubmission, signal?: AbortSignal) => Promise<ChatRequestAccepted>;
  getRequestStatus: (requestId: string, signal?: AbortSignal) => Promise<ChatRequestStatus>;
  /**
   * Follows a request over SSE. Resolves when the server closes the stream; rejects with
   * AssistantStreamUnavailableError if there is no stream to read, AssistantStreamFailedError if the
   * server reports an error on it, or the network / abort error.
   */
  streamRequest: (requestId: string, handlers: AssistantStreamHandlers, signal: AbortSignal) => Promise<void>;
  /**
   * Follows a whole session: one stream carrying every request's events, which stays open between them.
   * Resolves when the server closes it (the caller reconnects), rejects like streamRequest.
   *
   * Three things the per-request stream does not do (from the endpoint's own description): it does not
   * close when a request finishes, disconnecting does NOT cancel the running request — that is
   * `cancelRequest` — and events published while nobody was attached are gone, so a client that
   * re-attaches mid-request reads the answer back with `getRequestStatus` instead.
   */
  streamSession: (sessionId: string, handlers: AssistantSessionStreamHandlers, signal: AbortSignal) => Promise<void>;
  cancelRequest: (requestId: string) => Promise<ChatRequestStatus>;
  getSession: (sessionId: string, pageNumber: number, pageSize: number, signal?: AbortSignal) => Promise<ChatSessionView>;
}

export const createAssistantApi = ({ getToken, refreshToken }: AssistantApiOptions): AssistantApi => {
  /** fetch with the bearer token; on 401 the identity token may have expired — refreshed once and retried, as api.tsx#executeAuthorized does. */
  const authorizedFetch = async (method: string, url: string, accept: string, body?: unknown, signal?: AbortSignal): Promise<Response> => {
    const send = (bearer: string | undefined) =>
      fetch(url, {
        method,
        signal,
        headers: {
          Accept: accept,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

    const response = await send(await getToken());
    if (response.status !== 401) return response;
    log.debug('Assistant returned 401, refreshing identity token and retrying once');
    return send(await refreshToken());
  };

  /**
   * Opens an SSE endpoint and hands each event to `onEvent`, already JSON-parsed (null for a comment
   * or unparsable data). Rejects with AssistantStreamUnavailableError when there is no stream to read.
   */
  const openEventStream = async (
    url: string,
    signal: AbortSignal,
    onOpen: (() => void) | undefined,
    onEvent: (event: SseEvent | null, data: Record<string, unknown> | null) => void,
  ): Promise<void> => {
    const response = await authorizedFetch('GET', url, 'text/event-stream', undefined, signal);
    const contentType = response.headers.get('content-type');
    if (!response.ok || !response.body || !contentType?.includes('text/event-stream')) {
      // Not a stream (an error, or a proxy/backend answering in one piece) — nothing to read here.
      response.body?.cancel().catch(() => undefined);
      throw new AssistantStreamUnavailableError(response.status, contentType);
    }
    onOpen?.();

    await readSseStream(response.body, event => {
      if (!event) return onEvent(null, null);
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(event.data);
      } catch {
        log.debug('Assistant stream event with non-JSON data, ignored', { url, event: event.event, data: event.data });
        return;
      }
      // Outside the try: an error thrown by the handler means "stop the stream", not "bad data".
      onEvent(event, data);
    });
  };

  const call = async <T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> => {
    const url = `${ASSISTANT_BASE_URL}${path}`;
    const response = await authorizedFetch(method, url, 'application/json', body, signal);

    if (!response.ok) {
      const text = await response.text().catch(() => undefined);
      throw new AssistantApiError(response.status, url, text);
    }

    return (await response.json()) as T;
  };

  return {
    submitRequest: (body, signal) => call<ChatRequestAccepted>('POST', '/chat/requests', body, signal),
    getRequestStatus: (requestId, signal) => call<ChatRequestStatus>('GET', `/chat/requests/${requestId}`, undefined, signal),
    streamRequest: (requestId, { onOpen, onStep, onToken, onStatus, onAlive }, signal) =>
      openEventStream(`${ASSISTANT_BASE_URL}/chat/requests/${requestId}/stream`, signal, onOpen, (event, data) => {
        onAlive();
        if (!event) return;
        if (typeof data?.status === 'string') return onStatus(data as unknown as ChatRequestStatus);
        if (event.event === 'token' && typeof data?.text === 'string') return onToken(data.text);
        if (event.event === 'status' && typeof data?.step === 'string') return onStep(data.step);
        // An error with no status: the request may still finish (or have failed) — polling will tell.
        if (event.event === 'error') throw new AssistantStreamFailedError(event.data);
        log.debug('Assistant stream event not understood, ignored', { requestId, event: event.event, data: event.data });
      }),
    streamSession: (sessionId, { onOpen, onAttached, onStep, onToken, onStatus, onRequestError, onAlive }, signal) =>
      openEventStream(`${ASSISTANT_BASE_URL}/chat/sessions/${sessionId}/stream`, signal, onOpen, (event, data) => {
        onAlive();
        if (!event) return;
        // Events name the request they belong to where the server says so; the caller matches them to
        // the question it is waiting for.
        const requestId = typeof data?.request_id === 'string' ? data.request_id : undefined;
        if (event.event === 'snapshot') return onAttached(data ?? {});
        if (typeof data?.status === 'string') return onStatus(data as unknown as ChatRequestStatus);
        if (event.event === 'token' && typeof data?.text === 'string') return onToken(data.text, requestId);
        if (event.event === 'status' && typeof data?.step === 'string') return onStep(data.step, requestId);
        // One request's error must not tear down the session stream — the others keep running.
        if (event.event === 'error') return onRequestError(requestId, event.data);
        log.debug('Assistant session stream event not understood, ignored', { sessionId, event: event.event, data: event.data });
      }),
    cancelRequest: requestId => call<ChatRequestStatus>('DELETE', `/chat/requests/${requestId}`),
    getSession: (sessionId, pageNumber, pageSize, signal) =>
      call<ChatSessionView>('GET', `/chat/sessions/${sessionId}?pageNumber=${pageNumber}&pageSize=${pageSize}`, undefined, signal),
  };
};
